import React, { type FC, useEffect, useState } from 'react';
import { DatePicker, Modal, Space, InputNumber, InputText, Select } from '@g2u/ui-kit';
import { format, parse } from 'date-fns';
import { DefaultOptionType } from 'antd/es/select';
import { observer } from 'mobx-react-lite';
import { isEmpty } from 'lodash';
import { Form } from 'antd';

import { JsonData, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { validateFormSilently } from 'helpers';
import styles from './add-service-modal.module.scss';
import Big from 'big.js';
import { calculateTotalHours } from 'helpers/hooks/useTotalHours';

export namespace AddServiceModal {
  export type Props = {
    isOpen: boolean;
    selectedProducts?: JsonData;
    currentProductId?: string;
    productList: DefaultOptionType[] | undefined;
    onOk: (product: JsonDataItem) => void;
    onCancel: () => void;
    isGeekcoverMember: boolean;
  } & WithOptionalDataTest;

  export const $: FC<Props> = observer((props) => {
    const { isOpen, onOk, onCancel, selectedProducts, currentProductId, productList, dataTestId, isGeekcoverMember } =
      props;
    const [selectedProductId, setSelectedProductId] = useState<string | undefined>();
    const [selectedProduct, setSelectedProduct] = useState<JsonDataItem>();
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [form] = Form.useForm();
    const qty = Form.useWatch('productQty', form);
    const isNew = currentProductId === '0';
    const techInfo =
      selectedProducts?.find((jobProduct: JsonDataItem) => jobProduct.id === currentProductId)?.details
        ?.service_technicians[0] || selectedProducts?.[0]?.details?.service_technicians[0];
    const appointment = techInfo?.appointment;

    const handleFormSubmit = (formData: JsonDataItem | null) => {
      onOk({
        id: selectedProductId,
        qty: Big(formData?.productQty).toFixed(2).toString(),
        details: {
          product: selectedProduct,
          // reproduce the  product data structure like in the attached to the job product
          service_technicians: [
            {
              appointment: format(formData?.productDate, 'yyyy-MM-dd HH:mm:ss'),
            },
          ],
        },
      });
      setSelectedProductId(undefined);
    };

    //the value that represents the item that is ommitted for GC cusotmers is 13
    const additional30MinChargeHome = '13';

    const handleFormChange = (changedField: JsonDataItem) => {
      if (Object.keys(changedField).includes('productId')) {
        const selected = isNew
          ? productList?.find((product: JsonDataItem) => product.value === changedField.productId)?.details
          : selectedProducts?.find((product: JsonDataItem) => product.id === changedField.productId)?.details;

        setSelectedProduct(selected);
      }

      void (!isEmpty(form.getFieldsValue()) && validateFormSilently(form, setIsFormValid));
    };

    useEffect(() => {
      if (currentProductId) {
        setSelectedProductId(currentProductId);
        setSelectedProduct(
          isNew
            ? productList?.find((product: JsonDataItem) => product.value === currentProductId)?.details
            : selectedProducts?.find((product: JsonDataItem) => product.id === currentProductId)?.details.product,
        );
      }
    }, [currentProductId]);

    useEffect(() => {
      if (selectedProductId && selectedProduct) {
        const quantity = form.getFieldValue('productQty');
        form.setFieldsValue({
          productPrice: selectedProduct ? `$${Big(selectedProduct.total).toFixed(2)} inc GST` : '',
          productDuration: calculateTotalHours([{ details: { product: selectedProduct }, qty: quantity }]),
        });
      }
    }, [selectedProductId, selectedProduct, qty]);

    return (
      <Modal.$
        open={isOpen}
        destroyOnClose
        maskClosable={false}
        title={isNew ? 'Add New Service' : ' Edit Service'}
        okText={isNew ? 'Add' : 'Save'}
        onOk={() => {
          form.submit();
        }}
        onCancel={onCancel}
        okButtonProps={{
          loading: false,
          disabled: !isFormValid,
        }}
        dataTestId={dataTestId ?? 'add-service-modal'}
      >
        <Form
          form={form}
          name="lost-job-form"
          layout="vertical"
          onValuesChange={handleFormChange}
          onFinish={handleFormSubmit}
          colon={false}
          preserve={false}
        >
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
            <Form.Item>
              <div>
                <div className={styles.serviceList__item}>
                  <Select.$
                    onChange={(value) => {
                      value && setSelectedProductId(value);
                    }}
                    disabled={!isNew}
                    width={384}
                    initialValue={isNew ? undefined : currentProductId}
                    style={{ width: '100%', marginRight: 8 }}
                    placeholder="Select a product"
                    optionFilterProp="children"
                    className={styles.serviceList__select}
                    showSearch
                    name="productId"
                    required
                    rules={[{ required: true }]}
                    label="Service: "
                    options={
                      isNew
                        ? productList
                            ?.filter((product) => {
                              return !selectedProducts
                                ?.filter((item) => item.id?.toString() !== currentProductId)
                                ?.map((selected) => selected.id)
                                .includes(product.value);
                            })
                            .filter((product) => !(isGeekcoverMember && product.value === additional30MinChargeHome))
                        : [
                            {
                              label: selectedProduct?.description,
                              value: selectedProduct?.ID.toString(),
                              details: selectedProduct,
                            },
                          ]
                    }
                  />
                </div>
                {selectedProduct && (
                  <div className={styles.serviceList__item}>
                    <InputNumber.$
                      label="Qty: "
                      required
                      rules={[{ required: true }]}
                      name="productQty"
                      initialValue={
                        selectedProducts?.find((product: JsonDataItem) => product.id === currentProductId)?.qty || '1'
                      }
                      min={1}
                      width={55}
                      step={1}
                      className={styles.serviceList__hours}
                      disabled={!selectedProduct.duration}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                    <DatePicker.$
                      disabled={selectedProduct.restrict_display_in_booking === '1'}
                      label="Date: "
                      required
                      rules={[{ required: true }]}
                      initialValue={
                        appointment ? (parse(appointment, 'yyyy-MM-dd HH:mm:ss', new Date()) as any) : new Date()
                      }
                      name={'productDate'}
                      allowClear={false}
                    />
                    {selectedProductId && selectedProductId !== '0' && (
                      <InputText.$
                        disabled
                        label="Unit Price: "
                        required
                        rules={[{ required: true }]}
                        name="productPrice"
                        initialValue={`$${Number(selectedProduct?.total).toFixed(2)} inc GST`}
                        min={1}
                        width={150}
                      />
                    )}
                    {selectedProductId && selectedProductId !== '0' && (
                      <InputText.$
                        disabled
                        label="Total Hours: "
                        required
                        rules={[{ required: true }]}
                        name="productDuration"
                        initialValue={`${calculateTotalHours([{ details: { product: selectedProduct }, qty }]) || '-'}`}
                        width={55}
                      />
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
          </Space.$>
        </Form>
      </Modal.$>
    );
  });
}
